/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/dvrdButton.scss';

import React, {CSSProperties, MouseEventHandler, useContext, useState} from "react";
import classNames from "classnames";
import {ElementPosition, OrnamentShape} from "../util/interfaces";
import {ControlContext} from "../util/controlContext";
import {convertColor, editColor} from "../util/colorUtil";

export interface DvrdButtonProps {
    onClick: MouseEventHandler;
    label: string;
    ornaments?: OrnamentShape | OrnamentShape[];
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    secondary?: boolean;
    red?: boolean;
    customBaseColor?: string;
    customContrastColor?: string;
}

export default function DvrdButton(props: DvrdButtonProps) {
    const context = useContext(ControlContext),
        {
            className, disabled, label, labelClassName, onClick, ornamentClassName, ornaments, red, secondary,
            customBaseColor, customContrastColor
        } = props;
    const [hover, setHover] = useState(false);

    function onMouseEnter() {
        setHover(true);
    }

    function onMouseLeave() {
        setHover(false);
    }

    function getContainerStyle(): CSSProperties {
        const style: CSSProperties = {
            gridTemplateColumns: '1fr', backgroundColor: getBackgroundColor(), color: getColor()
        };
        let hasLeft: boolean = false, hasRight: boolean = false;
        if (Array.isArray(ornaments))
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) hasLeft = true;
                else if (ornament.placement === ElementPosition.RIGHT) hasRight = true;
                if (hasLeft && hasRight) break;
            }
        else if (ornaments) {
            hasLeft = !ornaments.placement || ornaments.placement === ElementPosition.LEFT;
            hasRight = ornaments.placement === ElementPosition.RIGHT;
        }
        let columns: string = '1fr';
        if (hasRight) columns = `${columns} fit-content(100%)`
        if (hasLeft) columns = `fit-content(100%) ${columns}`;
        style.gridTemplateColumns = columns;
        return style;
    }

    function getBackgroundColor(): string {
        let color: string;
        if (disabled) color = 'color-gray-4';
        else if (customBaseColor) color = customBaseColor;
        else if (red) {
            if (secondary) color = 'color-red-secondary';
            else color = 'rgb(255,0,0)';
        } else if (secondary) color = context.contrastColor;
        else color = context.baseColor;
        color = convertColor(color);
        if (hover && !disabled) color = editColor(-.2, color);
        return color;
    }

    function getColor(): string {
        let color: string;
        if (disabled) color = 'color-gray-3';
        else if (customContrastColor) color = customContrastColor;
        else if (red) {
            if (secondary) color = 'red';
            else color = 'white';
        } else if (secondary) color = context.baseColor;
        else color = context.contrastColor;
        return convertColor(color);
    }

    return (
        <div
            className={classNames('dvrd-button', disabled && 'disabled', secondary && 'secondary', red && 'red', className)}
            style={getContainerStyle()} onClick={disabled ? undefined : onClick} onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>
            <Ornament position={ElementPosition.LEFT} ornamentClassName={ornamentClassName} ornaments={ornaments}/>
            <label className={classNames('dvrd-button-label', labelClassName)}>{label}</label>
            <Ornament position={ElementPosition.RIGHT} ornamentClassName={ornamentClassName} ornaments={ornaments}/>
        </div>
    )
}

interface OrnamentProps {
    position: ElementPosition;
    ornaments?: OrnamentShape | OrnamentShape[];
    ornamentClassName?: string;
}

function Ornament(props: OrnamentProps) {
    const {ornaments, position, ornamentClassName} = props;
    if (!ornaments || (Array.isArray(ornaments) && !ornaments.length)) return null;
    let ornament: OrnamentShape | null = null;
    if (Array.isArray(ornaments)) {
        for (const orn of ornaments) {
            if (!orn.placement || orn.placement === position) {
                ornament = orn;
                break;
            }
        }
    } else if ((!ornaments.placement && position === ElementPosition.LEFT) || ornaments.placement === position)
        ornament = ornaments;
    if (!ornament) return null;

    if (typeof ornament.element === 'string')
        return <span className={classNames('dvrd-button-ornament', ornamentClassName)}>{ornament.element}</span>;
    else return React.cloneElement(ornament.element, {
        className: classNames('dvrd-button-ornament', ornament.element.props.className, ornamentClassName)
    });
}